import { useState } from 'react';

import { ApiError } from '../../../shared/api/errors';
import { Button } from '../../../shared/ui/button';
import { Hint } from '../../../shared/ui/hint';
import { Banner } from '../../../shared/ui/layout';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '../../../shared/ui/menu';
import { Note } from '../../../shared/ui/note';
import { SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import {
  BuiltinTag,
  isReplayable,
  ReplayNote,
  type TAuthPresetSpec,
} from '../../../entities/auth-preset';
import type { TSource, TSourceAuthMethod } from '../../../entities/source';
import type { ISourceScheme } from '../use-source-scheme';
import { HandBuiltDialog } from './hand-built-dialog';
import { SchemeDialog } from './scheme-dialog';
import { SenderPicker } from './sender-picker';
import { StopChecking } from './stop-checking';

const METHOD_NAMES: Record<TSourceAuthMethod['method'], string> = {
  hmac: 'A signature',
  basic: 'Basic auth',
  api_key: 'An API key',
  ip_allowlist: 'Sender addresses',
};

const methodDetail = (method: TSourceAuthMethod): string | null => {
  if (method.method === 'api_key') return method.authHeader ?? null;
  if (method.method !== 'ip_allowlist') return method.presetName ?? null;
  const parts = [
    ...(method.useProviderRanges && method.providerRangesKey
      ? [`the ${method.providerRangesKey} list`]
      : []),
    ...((method.manualCidrs?.length ?? 0) > 0 ? [`${method.manualCidrs?.length} of your own`] : []),
  ];
  return parts.length > 0 ? parts.join(' + ') : null;
};

const windowText = (seconds: number): string =>
  seconds % 60 === 0 ? `${seconds / 60} min window` : `${seconds} s window`;

const facts = (spec: TAuthPresetSpec, method: TSourceAuthMethod): string[] => {
  const parts = [spec.signature.location.header, spec.signature.algorithm];
  const tolerance = method.timestampToleranceSec ?? spec.timestamp.defaultToleranceSec ?? null;
  if (spec.timestamp.location.kind !== 'none' && tolerance) parts.push(windowText(tolerance));
  return parts;
};

const MethodOnly = ({ method }: { method: TSourceAuthMethod }) => (
  <div className="px-3 py-3">
    <p className="text-data font-medium text-primary">{METHOD_NAMES[method.method]}</p>
    {method.authHeader && (
      <p className="mt-1 font-mono text-meta text-muted">{method.authHeader}</p>
    )}
    <Hint>
      This source checks with settings of its own rather than with a published scheme, so there is
      no vendor here to name.
    </Hint>
  </div>
);

const Scheme = ({
  spec,
  method,
  name,
  builtin,
  onOpen,
}: {
  spec: TAuthPresetSpec;
  method: TSourceAuthMethod;
  name: string;
  builtin: boolean;
  onOpen?: () => void;
}) => (
  <div className="px-3 py-3">
    <div className="flex items-center gap-2">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="text-data font-medium text-primary hover:underline"
        >
          {name}
        </button>
      ) : (
        <span className="text-data font-medium text-primary">{name}</span>
      )}
      {builtin && <BuiltinTag />}
    </div>
    <p className="mt-1 font-mono text-meta text-muted">{facts(spec, method).join(' · ')}</p>

    {isReplayable(spec) && <ReplayNote name={name} className="mt-3" />}
  </div>
);

const ChangeMenu = ({
  onPick,
  onByHand,
  onStop,
  canStop,
}: {
  onPick: () => void;
  onByHand: () => void;
  onStop: () => void;
  canStop: boolean;
}) => (
  <Menu>
    <MenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-control-xs">
        Change
      </Button>
    </MenuTrigger>
    <MenuContent align="end" className="w-menu-lg">
      <MenuLabel>How this source is checked</MenuLabel>
      <MenuItem
        description="Pick another scheme from the catalogue. The secret stays."
        onSelect={onPick}
      >
        Use a different sender
      </MenuItem>
      <MenuItem description="Keep verifying, but describe the scheme yourself." onSelect={onByHand}>
        Set it up by hand instead
      </MenuItem>
      {canStop && (
        <>
          <MenuSeparator />
          <MenuItem
            tone="danger"
            description="Anything that reaches the address gets in."
            onSelect={onStop}
          >
            Stop checking signatures
          </MenuItem>
        </>
      )}
    </MenuContent>
  </Menu>
);

const stagedLine = (staged: string, current: string | null): string =>
  current === null
    ? `${staged} is picked. Nothing checks this source until you press Save at the top.`
    : `${staged} is picked. Requests are still checked with ${current} until you press Save at the top.`;

export const SchemeBlock = ({
  source,
  scheme,
  className,
}: {
  source: TSource;
  scheme: ISourceScheme;
  className?: string;
}) => {
  const { picking, setPicking } = scheme;
  const [byHand, setByHand] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [opening, setOpening] = useState(false);

  const current =
    scheme.method === null ? null : (scheme.senderName ?? METHOD_NAMES[scheme.method.method]);
  const others = scheme.enabledMethods.filter((method) => method.id !== scheme.method?.id);

  const stage = (staged: Parameters<typeof scheme.stage>[0]) => {
    scheme.stage(staged);
    setPicking(false);
  };

  return (
    <SectionPanel
      className={className}
      title="How it is checked"
      actions={
        scheme.isPending || scheme.isError ? null : scheme.checked ? (
          <ChangeMenu
            onPick={() => setPicking(true)}
            onByHand={() => setByHand(true)}
            // Only a signature check can be stopped; an address filter goes on filtering afterwards.
            canStop={scheme.hmacMethodId !== null}
            onStop={() => setStopping(true)}
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-control-xs"
            onClick={() => setPicking(true)}
          >
            Add checking
          </Button>
        )
      }
    >
      {scheme.isError && <ErrorState error={scheme.error} onRetry={scheme.refetch} />}
      {scheme.isPending && !scheme.isError && <LoadingState rows={2} />}

      {!scheme.isPending && !scheme.isError && (
        <>
          {scheme.method === null && (
            <div className="flex items-center gap-inset px-3 py-3">
              <span aria-hidden className="size-dot shrink-0 rounded-full bg-danger" />
              <span className="min-w-0">
                <span className="block text-data font-medium text-primary">Not checked</span>
                <span className="block text-meta text-muted">
                  anything that reaches the address is accepted
                </span>
              </span>
            </div>
          )}

          {scheme.method !== null &&
            (scheme.spec === null ? (
              <MethodOnly method={scheme.method} />
            ) : (
              <Scheme
                spec={scheme.spec}
                method={scheme.method}
                name={scheme.preset?.name ?? scheme.spec.name}
                builtin={scheme.preset?.builtin ?? false}
                onOpen={scheme.preset ? () => setOpening(true) : undefined}
              />
            ))}

          {others.length > 0 && (
            <div className="border-t border-border-sunken px-3 py-3">
              <p className="text-meta text-muted">
                {source.authCombine === 'all'
                  ? `All ${others.length + 1} checks have to pass`
                  : `Any one of ${others.length + 1} checks is enough — the rest may fail`}
              </p>
              {others.map((method) => (
                <p key={method.id} className="mt-1 font-mono text-meta text-primary">
                  {METHOD_NAMES[method.method]}
                  {methodDetail(method) && (
                    <span className="text-muted"> · {methodDetail(method)}</span>
                  )}
                </p>
              ))}
            </div>
          )}

          {scheme.staged && (
            <Note className="mx-3 mb-3">{stagedLine(scheme.staged.name, current)}</Note>
          )}

          {scheme.save.isError && (
            <div className="px-3 pb-3">
              <Banner
                detail={scheme.save.error instanceof ApiError ? scheme.save.error.code : undefined}
              >
                {scheme.save.error.message}
              </Banner>
            </div>
          )}
        </>
      )}

      <SenderPicker
        open={picking}
        onOpenChange={setPicking}
        currentPresetId={scheme.preset?.id ?? null}
        stagedPresetId={scheme.staged?.presetId ?? null}
        onStage={scheme.stage}
        onByHand={() => {
          setPicking(false);
          setByHand(true);
        }}
      />

      <HandBuiltDialog
        open={byHand}
        onOpenChange={setByHand}
        onStaged={stage}
        hasSecret={scheme.hasSecret}
      />

      <StopChecking open={stopping} onOpenChange={setStopping} source={source} scheme={scheme} />

      {scheme.preset && scheme.spec && scheme.method?.presetVersion != null && (
        <SchemeDialog
          open={opening}
          onOpenChange={setOpening}
          preset={scheme.preset}
          version={scheme.method.presetVersion}
          spec={scheme.spec}
          onCatalogue={() => setPicking(true)}
        />
      )}
    </SectionPanel>
  );
};
